import { getElementByXPath, getElementXPath } from '@/util/xpath';
import { bus } from '@/main';

export default {
  methods: {
    hexToRgb(hexColor) {
      if (!hexColor || typeof hexColor !== 'string') return null;
      const hex = hexColor.replace('#', '');
      if (hex.length !== 6) return null;
      const parsed = Number.parseInt(hex, 16);
      if (Number.isNaN(parsed)) return null;
      return {
        r: (parsed >> 16) & 255,
        g: (parsed >> 8) & 255,
        b: parsed & 255,
      };
    },
    highlightRange(range, id, isLinked, isTemporary, colorCode) {
      if (!this.isValidRange(range)) return false;

      let pageNodes = this.getPageNodesInRange(range);
      if (pageNodes.length === 0) {
        console.info('Unable to find pages from range', range);
        return;
      }

      let clientRects = range.getClientRects();
      // Group rects by line (vertical overlap) and merge into one rect per line
      let filteredRects = Array.from(clientRects)
        .filter((rect) => rect.width > 0)
        .reduce((lines, rect) => {
          // Two rects are on the same line if they overlap vertically by more than half
          let line = lines.find((l) => {
            let overlapTop = Math.max(l.top, rect.top);
            let overlapBottom = Math.min(l.bottom, rect.bottom);
            let overlap = overlapBottom - overlapTop;
            let minHeight = Math.min(l.bottom - l.top, rect.bottom - rect.top);
            return overlap > minHeight * 0.5;
          });
          if (!line) {
            lines.push({ top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom });
          } else {
            line.left = Math.min(line.left, rect.left);
            line.right = Math.max(line.right, rect.right);
            line.top = Math.min(line.top, rect.top);
            line.bottom = Math.max(line.bottom, rect.bottom);
          }
          return lines;
        }, [])
        .map((line) => ({
          left: line.left,
          top: line.top,
          width: line.right - line.left,
          height: line.bottom - line.top,
        }));

      let anchor = false;
      for (let { left, top, height, width } of filteredRects) {
        // Check to make sure the selection dimensions aren't zero or if a rect height is too tall, meaning it's a page
        if (!width || !height || height > 200) continue;

        // Find in which page container we need to add the selection element
        let pageNode = pageNodes.find((node) => {
          let pageRect = node.getBoundingClientRect();
          return top >= pageRect.top && top < pageRect.top + pageRect.height;
        });

        if (!pageNode) {
          console.error('Unable to find parent page for top position:' + top + '.');
          return;
        }

        let pageRect = pageNode.getBoundingClientRect();

        const selectionBox = document.createElement('div');
        selectionBox.classList.add('pdf-viewer__annotation');
        if (isTemporary) selectionBox.classList.add('temporary');
        selectionBox.classList.add('annotation-' + id);
        const colorRgb = this.hexToRgb(colorCode);
        if (colorRgb) {
          selectionBox.style.setProperty('--annotation-bg', `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.4)`);
          selectionBox.style.setProperty(
            '--annotation-bg-selected',
            `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.6)`,
          );
          selectionBox.style.setProperty(
            '--annotation-bg-temporary',
            `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.3)`,
          );
        }

        const annotationAnchor = document.createElement('div');
        annotationAnchor.style.display = 'none';
        annotationAnchor.classList.add('pdf-viewer__annotation-anchor');
        annotationAnchor.classList.add('annotation-anchor-' + id);
        if (colorRgb) {
          annotationAnchor.style.setProperty(
            '--annotation-anchor-bg',
            `rgb(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b})`,
          );
          annotationAnchor.style.setProperty(
            '--annotation-anchor-bg-selected',
            `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.85)`,
          );
        }
        // Only the first rect of filteredRects has to have an anchor
        if (!anchor) {
          anchor = true;
          annotationAnchor.style.top = top - pageRect.top + 'px';
          annotationAnchor.style.left = '-6px';
          if (isLinked) annotationAnchor.style.display = 'block';
        }
        selectionBox.style.top = top - pageRect.top + 'px';
        selectionBox.style.left = left - pageRect.left + 'px';
        selectionBox.style.width = width + 'px';
        selectionBox.style.height = height + 'px';

        let textSelectionsEl = pageNode.querySelector('.pdf-viewer__annotations-layer');
        if (!textSelectionsEl) {
          textSelectionsEl = document.createElement('div');
          textSelectionsEl.classList.add('pdf-viewer__annotations-layer');

          // Insert text selections layer before textLayer
          pageNode.insertBefore(textSelectionsEl, pageNode.querySelector('.textLayer'));
        }
        textSelectionsEl.appendChild(selectionBox);
        if (annotationAnchor) textSelectionsEl.appendChild(annotationAnchor);
      }
    },
    removeHighlightRange(annotation) {
      const id = annotation.id ? annotation.id : 'temporary';
      const page = annotation.startPage;

      const annotationLayer = document.querySelector(`#page-${page} .pdf-viewer__annotations-layer`);
      const annotations = document.querySelectorAll(`.annotation-${id}`);
      const annotationsAnchor = document.querySelectorAll(`.annotation-anchor-${id}`);

      if (annotationLayer && annotations.length > 0) {
        annotations.forEach((annotation) => {
          annotationLayer.removeChild(annotation);
        });
      }
      if (annotationLayer && annotationsAnchor.length > 0) {
        annotationsAnchor.forEach((anchor) => {
          annotationLayer.removeChild(anchor);
        });
      }
    },
    isTemporaryAnnotation() {
      const temporaryElements = document.querySelectorAll('.temporary');
      return temporaryElements.length > 0;
    },
    isValidRange(range) {
      let textNodes = this.getTextNodesInRange(range);
      return textNodes.length > 0 && textNodes.some(this.isNonEmptyTextNode);
    },
    isTextNode(node) {
      return node.nodeType === Node.TEXT_NODE;
    },
    isNonEmptyTextNode(node) {
      return this.isTextNode(node) && node.nodeValue.trim().length > 0;
    },
    getTextNodesInRange(range) {
      return this.getNodesInRange(range).reduce((textNodes, node) => {
        if (this.isTextNode(node)) {
          textNodes.push(node);
        }
        return textNodes;
      }, []);
    },
    getPageNodesInRange(range) {
      let startPage = this.findAncestor(range.startContainer, '.pdf-viewer__page');
      let endPage = this.findAncestor(range.endContainer, '.pdf-viewer__page');
      return this.getRangeElements(startPage, endPage, '.pdf-viewer__page');
    },
    getRangeElements(startEl, endEl, filter) {
      // Setup siblings array
      let siblings = [];

      // As long as a sibling exists
      while (startEl) {
        // If filtering by a selector, check if the sibling matches
        if (!filter || startEl.matches(filter)) {
          // Otherwise, push it to the siblings array
          siblings.push(startEl);
        }

        // If we've reached our match, bail
        if (startEl.isSameNode(endEl)) break;

        // Get the next sibling element
        startEl = startEl.nextElementSibling;
      }

      return siblings;
    },
    getNodesInRange(range) {
      let start = range.startContainer;
      let end = range.endContainer;
      let commonAncestor = range.commonAncestorContainer;
      let nodes = [];
      let node;

      // Walk parent nodes from start to common ancestor
      for (node = start.parentNode; node; node = node.parentNode) {
        nodes.push(node);
        if (node === commonAncestor) break;
      }
      nodes.reverse();

      // Walk children and siblings from start until end is found
      for (node = start; node; node = this.getNextNode(node)) {
        nodes.push(node);
        if (node === end) break;
      }

      return nodes;
    },
    getNextNode(node) {
      let next = node.firstChild;
      if (next) return next;

      while (node) {
        if ((next = node.nextSibling)) {
          return next;
        }
        node = node.parentNode;
      }
    },
    clearSelectionRanges() {
      if (window.getSelection) {
        if (window.getSelection().empty) {
          // Chrome
          window.getSelection().empty();
        } else if (window.getSelection().removeAllRanges) {
          // Firefox
          window.getSelection().removeAllRanges();
        }
      } else if (document.selection) {
        // IE
        document.selection.empty();
      }
    },
    findAncestor(el, sel) {
      el = el.parentElement;
      while (el && !el.matches(sel)) {
        el = el.parentElement;
      }
      return el;
    },
    getRangeFromSelection(selection) {
      if (!selection) return null;

      let startContainer = getElementByXPath(selection.startXPath);
      let startOffset = selection.startOffset;
      let endContainer = getElementByXPath(selection.endXPath);
      let endOffset = selection.endOffset;

      if (!startContainer || !endContainer) return null;

      try {
        let range = document.createRange();
        range.setStart(startContainer, startOffset);
        range.setEnd(endContainer, endOffset);
        return range;
      } catch (e) {
        return null;
      }
    },
    stripKeywordElements(pageNumber) {
      const textLayer = document.getElementById(`page-${pageNumber}-text-layer`);
      if (!textLayer) return false;
      const keywords = textLayer.querySelectorAll('keyword');
      if (keywords.length === 0) return false;
      const parents = new Set();
      keywords.forEach((kw) => {
        const parent = kw.parentNode;
        parents.add(parent);
        while (kw.firstChild) parent.insertBefore(kw.firstChild, kw);
        parent.removeChild(kw);
      });
      parents.forEach((parent) => parent.normalize());
      return true;
    },
    createSelectionFromRange(selectionRange) {
      let startPageEl = this.findAncestor(selectionRange.startContainer, '.pdf-viewer__page.rendered');
      let endPageEl = this.findAncestor(selectionRange.endContainer, '.pdf-viewer__page.rendered');
      let startPage = parseInt(startPageEl.id.replace('page-', ''));
      let endPage = parseInt(endPageEl.id.replace('page-', ''));

      // Strip keyword highlights before generating XPaths so they don't reference split text nodes.
      // normalize() updates live Range boundaries automatically per DOM spec.
      const pages = [...new Set([startPage, endPage])];
      const stripped = pages.filter((page) => this.stripKeywordElements(page));

      const anyASCIIControlChar = /[\x00-\x1F\x7F-\x9F]/g;
      const selection = {
        '@type': 'HtmlTextSelection',
        uuid: this.$uuid.v4(),
        startPage: startPage,
        startXPath: getElementXPath(selectionRange.startContainer),
        startOffset: selectionRange.startOffset,
        endPage: endPage,
        endXPath: getElementXPath(selectionRange.endContainer),
        endOffset: selectionRange.endOffset,
        text: selectionRange.toString().replace(anyASCIIControlChar, ''),
        color: '232, 202, 238',
        colorCode: null,
      };

      // Re-render keyword highlights
      if (stripped.length > 0) {
        bus.$emit('re-render-keywords', stripped);
      }

      return selection;
    },
    getSelectionUuidFromCoordinates(targetNode, clickX, clickY) {
      let parentPage = this.findAncestor(targetNode, '.pdf-viewer__page');
      if (!parentPage) return false;

      let pageRect = parentPage.getBoundingClientRect();
      let pageYOffset = window.pageYOffset;
      let pageXOffset = window.pageXOffset;
      // Find the targeted text selection using mouse click coordinates
      let textSelections = parentPage.querySelectorAll('.pdf-viewer__annotation');
      let focusedSelection = Array.from(textSelections).find((sel) => {
        let rect = sel.getBoundingClientRect();
        return (
          clickX - pageXOffset - pageRect.left >= sel.offsetLeft &&
          clickX - pageXOffset - pageRect.left <= sel.offsetLeft + rect.width &&
          clickY - pageYOffset - pageRect.top >= sel.offsetTop &&
          clickY - pageYOffset - pageRect.top <= sel.offsetTop + rect.height
        );
      });

      if (!focusedSelection) return null;
      let selClass = this.getElementSelectionClass(focusedSelection);
      if (!selClass) return null;
      return selClass.replace('annotation-', '');
    },
    getElementSelectionClass(element) {
      return [...element.classList].find((cls) => cls.startsWith('annotation-'));
    },
  },
};
